import { Icon } from "@freightsimple/ui-core/Icons/Icon";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { T } from "@freightsimple/ui-core/Typography/T";
import type { ComponentType } from "react";
import { ArrowsLeftRightIcon } from "@phosphor-icons/react/ArrowsLeftRight";
import { BankIcon } from "@phosphor-icons/react/Bank";
import { CreditCardIcon } from "@phosphor-icons/react/CreditCard";
import { GlobeIcon } from "@phosphor-icons/react/Globe";
import { MoneyIcon } from "@phosphor-icons/react/Money";
import { PaperPlaneTiltIcon } from "@phosphor-icons/react/PaperPlaneTilt";
import { SignatureIcon } from "@phosphor-icons/react/Signature";
import type { PaymentMethodsState } from "../Types/paymentsPaymentTypes";

// Phosphor glyphs, not the credit-card-brand image files: this component names
// the KIND of method, not a card brand, so it takes the same icon system as
// the rest of the renovated UI. There is no cheque glyph in Phosphor — a
// signature is the closest thing to a signed piece of paper.
const IconAndTextComponent_ = ({
  paymentMethod,
  icon,
}: {
  paymentMethod: string;
  icon: ComponentType<{ className?: string }>;
}) => {
  return (
    <Row gap={1}>
      <Icon icon={icon} size="md" weight="regular" />
      <T>{paymentMethod}</T>
    </Row>
  );
};

interface PaymentMethodWithIconProps {
  selectedPaymentMethod: string | undefined;
  paymentMethodsState: PaymentMethodsState;
}

export const PaymentsMethodWithIcon = ({
  selectedPaymentMethod,
  paymentMethodsState,
}: PaymentMethodWithIconProps) => {
  if (!selectedPaymentMethod) return undefined;

  const isBankAccount = paymentMethodsState.bankAccounts.some(
    (ba) => ba.stripePaymentMethodId === selectedPaymentMethod,
  );

  if (isBankAccount) {
    return (
      <IconAndTextComponent_ paymentMethod="Bank Account" icon={BankIcon} />
    );
  }

  const isCreditCard = paymentMethodsState.creditCards.some(
    (cc) => cc.stripePaymentMethodId === selectedPaymentMethod,
  );

  if (isCreditCard) {
    return (
      <IconAndTextComponent_
        paymentMethod="Credit Card"
        icon={CreditCardIcon}
      />
    );
  }

  switch (selectedPaymentMethod) {
    case "cheque":
      return (
        <IconAndTextComponent_ paymentMethod="Cheque" icon={SignatureIcon} />
      );
    case "etransfer":
      return (
        <IconAndTextComponent_
          paymentMethod="Interac E-Transfer"
          icon={PaperPlaneTiltIcon}
        />
      );
    case "eft":
      return (
        <IconAndTextComponent_
          paymentMethod="EFT/ACH"
          icon={ArrowsLeftRightIcon}
        />
      );
    case "wire":
      return <IconAndTextComponent_ paymentMethod="Wire" icon={GlobeIcon} />;

    case "cash":
      return <IconAndTextComponent_ paymentMethod="Cash" icon={MoneyIcon} />;
    default:
      return <></>;
  }
};
